/**
 * ═══════════════════════════════════════════════════════════════════════
 * SetPasswordPage — Force Change Password
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Shown when a manually-created Cognito user logs in for the first time
 * and must set a new password (NEW_PASSWORD_REQUIRED challenge).
 *
 * The user arrives here from LoginPage via navigate(ROUTES.SET_PASSWORD,
 * { state: { username, session } }). `session` is the Cognito challenge
 * session from that login — the backend refuses set-password without it.
 *
 * Flow:
 *   1. Enter new password + confirm
 *   2. Call authApi.setPassword({ userType, username, password, session })
 *   3. Backend completes the NEW_PASSWORD_REQUIRED challenge → tokens
 *   4. Store session, route to agent page
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { setPassword as setPasswordApi } from '../services/auth/authApi';
import { getAuthErrorMessage } from '../services/auth/authErrors';
import { TIMING } from '../constants';
import { ROUTES } from '../constants/routes';

import AuthCard from '../components/auth/AuthCard';
import PasswordInput from '../components/auth/PasswordInput';
import PrimaryButton from '../components/auth/PrimaryButton';

// ── Helpers ──────────────────────────────────────────────────────────

function validatePassword(pw) {
  if (!pw) return 'Enter a password';
  if (pw.length < 8) return 'At least 8 characters';
  if (!/[A-Z]/.test(pw)) return 'Include an uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Include a lowercase letter';
  if (!/\d/.test(pw)) return 'Include a number';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Include a special character';
  return '';
}

// ── Component ────────────────────────────────────────────────────────

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const auth = useAuth();

  const usernameFromState = location.state?.username || auth.username || '';
  const challengeSession = location.state?.session || '';

  // ── local form state ─────────────────────────────────────────────
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { userType } = auth;

  // ── validation ───────────────────────────────────────────────────
  const passwordError = useMemo(() => validatePassword(password), [password]);
  const confirmError = useMemo(
    () => (confirmPassword && confirmPassword !== password ? 'Passwords do not match' : ''),
    [password, confirmPassword],
  );

  const formValid = !passwordError && !confirmError && password.length > 0 && confirmPassword.length > 0;

  // No username or no login session → must log in again
  if (!usernameFromState || !challengeSession) {
    return (
      <AuthCard title="Set Password" subtitle="Session expired" error="No user information found. Please log in again.">
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Link to={ROUTES.LOGIN} style={{ color: theme.auth.buttonPrimary, textDecoration: 'none', fontWeight: 600 }}>
            Back to Login
          </Link>
        </div>
      </AuthCard>
    );
  }

  // ── submit ───────────────────────────────────────────────────────
  const handleSetPassword = async () => {
    setError('');
    if (!formValid) return;
    setLoading(true);
    try {
      const result = await setPasswordApi({
        userType,
        username: usernameFromState,
        password,
        session: challengeSession,
      });

      // Store JWT in memory so apiClient attaches it to subsequent requests
      if (result?.idToken) {
        const { setAuthToken } = await import('../services/api/apiClient');
        setAuthToken(result.idToken);
      }

      // Store session metadata
      const { clearStoredSession, storeSession, getSession, getStoredSession } = await import(
        '../services/api/sessionApi'
      );
      clearStoredSession();

      if (result) {
        try {
          storeSession(result);
        } catch (_) {
          /* non-critical */
        }
      }

      // Wait briefly for cookies to settle
      await new Promise((r) => setTimeout(r, TIMING.SESSION_STABILIZATION_DELAY));

      // Fetch authoritative session from backend
      let sessionData;
      try {
        sessionData = await getSession();
      } catch (sessionError) {
        // STAFF-DEVICE-LOCK (RC-2): a device refusal is TERMINAL and must not
        // fall back to a stored session — an admin resetting a locked member's
        // password is exactly when they set it from an unapproved device.
        // Revoke the Cognito tokens first, then let the outer catch render the
        // ruled sentence (authErrors maps both codes to it).
        if (
          sessionError?.message === 'DEVICE_NOT_APPROVED'
          || sessionError?.message === 'DEVICE_ID_MISSING'
        ) {
          const { signOut } = await import('../services/auth/authApi');
          await signOut();
          throw sessionError;
        }
        sessionData = getStoredSession();
        if (!sessionData) sessionData = result;
      }

      storeSession(sessionData);
      auth.loginSuccess({ accessToken: result?.accessToken || null });

      // Start proactive token refresh timer
      try {
        const { scheduleTokenRefresh } = await import('../services/api/apiClient');
        scheduleTokenRefresh();
      } catch (_) { /* non-critical */ }

      // Route by assigned agent
      if (sessionData.agent === 'crowchat') {
        navigate(ROUTES.ORG, { replace: true });
      } else {
        navigate(ROUTES.CHAT, { replace: true });
      }
    } catch (e) {
      setError(getAuthErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ── handle Enter key ─────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && formValid && !loading) {
      handleSetPassword();
    }
  };

  // ── render ───────────────────────────────────────────────────────
  return (
    <AuthCard
      title="Set Password"
      subtitle="You must set a new password to continue"
      error={error}
    >
      <div style={{ marginBottom: 16, fontSize: 14, color: theme.auth.labelColor }}>
        Setting password for <strong>{usernameFromState}</strong>
      </div>

      <div onKeyDown={handleKeyDown}>
        <PasswordInput
          value={password}
          onChange={setPassword}
          error={passwordError && password ? passwordError : ''}
          disabled={loading}
          label="New Password"
          placeholder="Create a strong password"
        />

        <div style={{ height: 12 }} />

        <PasswordInput
          value={confirmPassword}
          onChange={setConfirmPassword}
          error={confirmError}
          disabled={loading}
          label="Confirm Password"
          placeholder="Re-enter password"
        />
      </div>

      <div style={{ height: 16 }} />

      <PrimaryButton
        onClick={handleSetPassword}
        disabled={!formValid || loading}
      >
        {loading ? 'Setting password…' : 'Set Password'}
      </PrimaryButton>

      <div style={{ marginTop: 16, textAlign: 'center', fontSize: 14, color: theme.auth.mutedText }}>
        <Link to={ROUTES.LOGIN} style={{ color: theme.auth.buttonPrimary, textDecoration: 'none', fontWeight: 600 }}>
          Back to Login
        </Link>
      </div>
    </AuthCard>
  );
}
